import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, UserPlus, Loader2, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';
import ImageCropModal from '../../components/ImageCropModal';

const CreateUser = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [photoPreview, setPhotoPreview] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [showLogoCropModal, setShowLogoCropModal] = useState(false);
    const [logoToCrop, setLogoToCrop] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        roleKey: 'installer',
        phone: '',
        // OEM specific fields
        oemData: {
            companyName: '',
            contactPerson: '',
            contactPersonMobile: '',
            contactPersonDesignation: '',
            contactPersonEmail: '',
            address: '',
            billingAddress: '',
            gstNumber: '',
            panNumber: '',
            logoUrl: ''
        },
        // Employee specific fields
        employeeData: {
            designation: '',
            department: '',
            joiningDate: new Date().toISOString().split('T')[0],
            bloodGroup: '',
            employmentType: 'full_time',
            skillLevel: 'beginner',
            yearsOfExperience: 0,
            certifications: '',
            specialization: '',
            bankName: '',
            bankBranch: '',
            accountNumber: '',
            ifscCode: '',
            paymentMode: 'neft',
            panNumber: '',
            aadharNumber: '',
            emergencyContactName: '',
            emergencyContactRelation: '',
            emergencyContactPhone: '',
            currentAddress: '',
            permanentAddress: '',
            city: '',
            state: '',
            pinCode: '',
            faceReferenceUrl: '',
            faceRecognitionEnabled: false
        }
    });

    const roles = [
        { key: 'admin', label: 'Administrator' },
        { key: 'oem', label: 'OEM Partner' },
        { key: 'supervisor', label: 'SuperAligner' },
        { key: 'installer', label: 'Aligner' },
        { key: 'junioraligner', label: 'Junior Aligner' }
    ];

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        // Indian phone number: 10 digits, starting with 6-9
        const phoneRegex = /^[6-9]\d{9}$/;
        return phoneRegex.test(phone.replace(/\D/g, ''));
    };

    const handlePhotoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast({
                    title: 'Invalid File',
                    description: 'Please upload an image file',
                    variant: 'destructive'
                });
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast({
                    title: 'File Too Large',
                    description: 'Photo must be less than 5MB',
                    variant: 'destructive'
                });
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImageToCrop(reader.result);
                setShowCropModal(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = (croppedImage) => {
        setPhotoPreview(croppedImage);
        setFormData({
            ...formData,
            employeeData: { ...formData.employeeData, faceReferenceUrl: croppedImage }
        });
        setShowCropModal(false);
        setImageToCrop(null);
    };

    const handleCloseCropModal = () => {
        setShowCropModal(false);
        setImageToCrop(null);
        // Clear the file input
        const fileInput = document.getElementById('photoUpload');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast({
                    title: 'Invalid File',
                    description: 'Please upload an image file',
                    variant: 'destructive'
                });
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast({
                    title: 'File Too Large',
                    description: 'Logo must be less than 5MB',
                    variant: 'destructive'
                });
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoToCrop(reader.result);
                setShowLogoCropModal(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoCropComplete = (croppedImage) => {
        setLogoPreview(croppedImage);
        setFormData({
            ...formData,
            oemData: { ...formData.oemData, logoUrl: croppedImage }
        });
        setShowLogoCropModal(false);
        setLogoToCrop(null);
    };

    const handleCloseLogoCropModal = () => {
        setShowLogoCropModal(false);
        setLogoToCrop(null);
        // Clear the file input
        const fileInput = document.getElementById('logoUpload');
        if (fileInput) fileInput.value = '';
    };

    const handleRemovePhoto = () => {
        setPhotoPreview(null);
        setFormData({
            ...formData,
            employeeData: {
                ...formData.employeeData,
                faceReferenceUrl: null,
                faceRecognitionEnabled: false // Disable face recognition when photo is removed
            }
        });
        // Clear the file input
        const fileInput = document.getElementById('photoUpload');
        if (fileInput) fileInput.value = '';
    };

    const handleRemoveLogo = () => {
        setLogoPreview(null);
        setFormData({
            ...formData,
            oemData: { ...formData.oemData, logoUrl: null }
        });
        // Clear the file input
        const fileInput = document.getElementById('logoUpload');
        if (fileInput) fileInput.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const newErrors = {};

        // Validate: At least one of Email or Phone must be provided
        if (!formData.email && !formData.phone) {
            toast({
                title: 'Validation Error',
                description: 'Please provide either an email address or phone number',
                variant: 'destructive'
            });
            setSubmitting(false);
            return;
        }

        // Validate email format if provided
        if (formData.email && !validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }

        // Validate phone format if provided
        if (formData.phone && !validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number (10 digits, starting with 6-9)';
        }

        // Validate OEM mandatory fields
        if (formData.roleKey === 'oem') {
            if (!formData.oemData.companyName) {
                newErrors.companyName = 'Company Name is required';
            }
            if (!formData.oemData.contactPerson) {
                newErrors.contactPerson = 'Contact Person is required';
            }
        }

        // Validate Employee mandatory fields
        if (['supervisor', 'installer', 'junioraligner'].includes(formData.roleKey)) {
            if (!formData.employeeData.joiningDate) {
                newErrors.joiningDate = 'Joining Date is required for employees';
            }
        }

        // Show validation errors if any
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);

            // Scroll to first error field
            const firstErrorField = Object.keys(newErrors)[0];
            const errorElement = document.getElementById(firstErrorField);
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
            }

            toast({
                title: 'Validation Error',
                description: 'Please fix the errors below and try again',
                variant: 'destructive'
            });
            setSubmitting(false);
            return;
        }

        setErrors({});

        try {
            const userData = {
                email: formData.email || null,
                password: formData.password,
                name: formData.name,
                roleKey: formData.roleKey,
                phone: formData.phone || null
            };

            // Add OEM-specific data if role is 'oem'
            if (formData.roleKey === 'oem') {
                userData.oemData = {
                    companyName: formData.oemData.companyName,
                    contactPerson: formData.oemData.contactPerson,
                    contactPersonMobile: formData.oemData.contactPersonMobile || null,
                    contactPersonDesignation: formData.oemData.contactPersonDesignation || null,
                    contactPersonEmail: formData.oemData.contactPersonEmail || null,
                    address: formData.oemData.address || null,
                    billingAddress: formData.oemData.billingAddress || null,
                    gstNumber: formData.oemData.gstNumber || null,
                    panNumber: formData.oemData.panNumber || null,
                    logoUrl: formData.oemData.logoUrl || null
                };
            }

            // Add Employee-specific data if role is employee group
            if (['supervisor', 'installer', 'junioraligner'].includes(formData.roleKey)) {
                userData.employeeData = {
                    designation: formData.employeeData.designation || null,
                    department: formData.employeeData.department || null,
                    joiningDate: formData.employeeData.joiningDate || null,
                    bloodGroup: formData.employeeData.bloodGroup || null,
                    employmentType: formData.employeeData.employmentType || 'full_time',
                    skillLevel: formData.employeeData.skillLevel || 'beginner',
                    yearsOfExperience: parseInt(formData.employeeData.yearsOfExperience) || 0,
                    certifications: formData.employeeData.certifications || null,
                    specialization: formData.employeeData.specialization || null,
                    bankName: formData.employeeData.bankName || null,
                    bankBranch: formData.employeeData.bankBranch || null,
                    accountNumber: formData.employeeData.accountNumber || null,
                    ifscCode: formData.employeeData.ifscCode || null,
                    paymentMode: formData.employeeData.paymentMode || 'neft',
                    panNumber: formData.employeeData.panNumber || null,
                    aadharNumber: formData.employeeData.aadharNumber || null,
                    emergencyContactName: formData.employeeData.emergencyContactName || null,
                    emergencyContactRelation: formData.employeeData.emergencyContactRelation || null,
                    emergencyContactPhone: formData.employeeData.emergencyContactPhone || null,
                    currentAddress: formData.employeeData.currentAddress || null,
                    permanentAddress: formData.employeeData.permanentAddress || null,
                    city: formData.employeeData.city || null,
                    state: formData.employeeData.state || null,
                    pinCode: formData.employeeData.pinCode || null,
                    faceReferenceUrl: formData.employeeData.faceReferenceUrl || null,
                    faceRecognitionEnabled: formData.employeeData.faceRecognitionEnabled || false
                };
            }

            console.log('Creating user with data:', userData);
            await api.createUser(userData);

            toast({
                title: 'Success',
                description: 'User created successfully'
            });

            navigate('/admin/settings/users');
        } catch (error) {
            console.error('User creation failed:', error);
            toast({
                title: 'Error',
                description: error.message || 'Failed to create user',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                {/* Header */}
                <div className="mb-6">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/admin/users')}
                        className="mb-4"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Users
                    </Button>

                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                            <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create New User</h1>
                            <p className="text-sm sm:text-base text-gray-600">Add a new user to the system</p>
                        </div>
                    </div>
                </div>

                {/* Form Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>User Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Details</h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="name">Full Name *</Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="role">Role *</Label>
                                        <select
                                            id="role"
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                            value={formData.roleKey}
                                            onChange={(e) => setFormData({ ...formData, roleKey: e.target.value })}
                                        >
                                            {roles.map(role => (
                                                <option key={role.key} value={role.key}>{role.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="email">Email *</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => {
                                                setFormData({ ...formData, email: e.target.value });
                                                if (errors.email) {
                                                    const newErrors = { ...errors };
                                                    delete newErrors.email;
                                                    setErrors(newErrors);
                                                }
                                            }}
                                            className={errors.email ? 'border-red-500' : ''}
                                        />
                                        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                                    </div>
                                    <div>
                                        <Label htmlFor="phone">Phone *</Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => {
                                                // Only allow digits
                                                const value = e.target.value.replace(/\D/g, '');
                                                setFormData({ ...formData, phone: value });
                                                if (errors.phone) {
                                                    const newErrors = { ...errors };
                                                    delete newErrors.phone;
                                                    setErrors(newErrors);
                                                }
                                            }}
                                            placeholder="10 digit number (e.g., 9876543210)"
                                            maxLength={10}
                                            className={errors.phone ? 'border-red-500' : ''}
                                        />
                                        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 -mt-2">* At least one contact method (Email or Phone) is required</p>

                                <div>
                                    <Label htmlFor="password">Password *</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        minLength={6}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                                </div>
                            </div>

                            {/* OEM-specific fields */}
                            {formData.roleKey === 'oem' && (
                                <div className="space-y-4 border-t pt-6">
                                    <h3 className="text-lg font-semibold text-gray-900">Company Details</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="companyName">Company Name *</Label>
                                            <Input
                                                id="companyName"
                                                value={formData.oemData.companyName}
                                                onChange={(e) => {
                                                    setFormData({
                                                        ...formData,
                                                        oemData: { ...formData.oemData, companyName: e.target.value }
                                                    });
                                                    if (errors.companyName) {
                                                        const newErrors = { ...errors };
                                                        delete newErrors.companyName;
                                                        setErrors(newErrors);
                                                    }
                                                }}
                                                className={errors.companyName ? 'border-red-500' : ''}
                                            />
                                            {errors.companyName && <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>}
                                        </div>
                                        <div>
                                            <Label htmlFor="contactPerson">Contact Person *</Label>
                                            <Input
                                                id="contactPerson"
                                                value={formData.oemData.contactPerson}
                                                onChange={(e) => {
                                                    setFormData({
                                                        ...formData,
                                                        oemData: { ...formData.oemData, contactPerson: e.target.value }
                                                    });
                                                    if (errors.contactPerson) {
                                                        const newErrors = { ...errors };
                                                        delete newErrors.contactPerson;
                                                        setErrors(newErrors);
                                                    }
                                                }}
                                                className={errors.contactPerson ? 'border-red-500' : ''}
                                            />
                                            {errors.contactPerson && <p className="text-xs text-red-500 mt-1">{errors.contactPerson}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="contactPersonMobile">Contact Person Mobile</Label>
                                            <Input
                                                id="contactPersonMobile"
                                                type="tel"
                                                value={formData.oemData.contactPersonMobile}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    setFormData({
                                                        ...formData,
                                                        oemData: { ...formData.oemData, contactPersonMobile: value }
                                                    });
                                                }}
                                                maxLength={10}
                                                placeholder="10-digit mobile number"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="contactPersonDesignation">Designation</Label>
                                            <Input
                                                id="contactPersonDesignation"
                                                value={formData.oemData.contactPersonDesignation}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    oemData: { ...formData.oemData, contactPersonDesignation: e.target.value }
                                                })}
                                                placeholder="e.g., Manager, Director"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="contactPersonEmail">Contact Person Email</Label>
                                            <Input
                                                id="contactPersonEmail"
                                                type="email"
                                                value={formData.oemData.contactPersonEmail}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    oemData: { ...formData.oemData, contactPersonEmail: e.target.value }
                                                })}
                                                placeholder="contact@company.com"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="gstNumber">GST Number</Label>
                                            <Input
                                                id="gstNumber"
                                                value={formData.oemData.gstNumber}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    oemData: { ...formData.oemData, gstNumber: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="oemPanNumber">PAN Number</Label>
                                            <Input
                                                id="oemPanNumber"
                                                value={formData.oemData.panNumber}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    oemData: { ...formData.oemData, panNumber: e.target.value }
                                                })}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <Label htmlFor="address">Address</Label>
                                        <textarea
                                            id="address"
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                            rows="3"
                                            value={formData.oemData.address}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                oemData: { ...formData.oemData, address: e.target.value }
                                            })}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="billingAddress">Billing Address</Label>
                                        <textarea
                                            id="billingAddress"
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                            rows="3"
                                            value={formData.oemData.billingAddress}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                oemData: { ...formData.oemData, billingAddress: e.target.value }
                                            })}
                                        />
                                    </div>

                                    {/* Company Logo Upload Section */}
                                    <div className="border-t pt-4 mt-4">
                                        <h4 className="font-semibold text-gray-900 mb-3">Company Logo</h4>

                                        {/* Logo Preview */}
                                        {logoPreview && (
                                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 relative">
                                                <button
                                                    type="button"
                                                    onClick={handleRemoveLogo}
                                                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition"
                                                    title="Remove logo"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                                <p className="text-sm font-medium text-gray-700 mb-2">Logo Preview:</p>
                                                <img
                                                    src={logoPreview}
                                                    alt="Company Logo Preview"
                                                    className="h-24 w-auto object-contain rounded-lg border-2 border-blue-300"
                                                />
                                            </div>
                                        )}

                                        {/* Logo Upload */}
                                        <div>
                                            <Label htmlFor="logoUpload">Upload Company Logo</Label>
                                            <div className="flex items-center justify-center w-full">
                                                <label htmlFor="logoUpload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                        <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        <p className="text-sm text-gray-600">Click to upload company logo</p>
                                                        <p className="text-xs text-gray-500">PNG, JPG (Max 5MB)</p>
                                                    </div>
                                                    <input
                                                        id="logoUpload"
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={handleLogoUpload}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Employee-specific fields */}
                            {['supervisor', 'installer', 'junioraligner'].includes(formData.roleKey) && (
                                <div className="space-y-4 border-t pt-6">
                                    <h3 className="text-lg font-semibold text-gray-900">Employee Details</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="designation">Designation</Label>
                                            <Input
                                                id="designation"
                                                value={formData.employeeData.designation}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, designation: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="department">Department</Label>
                                            <select
                                                id="department"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                                value={formData.employeeData.department}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, department: e.target.value }
                                                })}
                                            >
                                                <option value="">Select Department</option>
                                                <option value="Installation">Installation</option>
                                                <option value="Accounts">Accounts</option>
                                                <option value="Administration">Administration</option>
                                                <option value="Sales">Sales</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="joiningDate">Joining Date *</Label>
                                            <Input
                                                id="joiningDate"
                                                type="date"
                                                value={formData.employeeData.joiningDate}
                                                onChange={(e) => {
                                                    setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, joiningDate: e.target.value }
                                                    });
                                                    if (errors.joiningDate) {
                                                        const newErrors = { ...errors };
                                                        delete newErrors.joiningDate;
                                                        setErrors(newErrors);
                                                    }
                                                }}
                                                className={errors.joiningDate ? 'border-red-500' : ''}
                                            />
                                            {errors.joiningDate && <p className="text-xs text-red-500 mt-1">{errors.joiningDate}</p>}
                                        </div>
                                        <div>
                                            <Label htmlFor="bloodGroup">Blood Group</Label>
                                            <select
                                                id="bloodGroup"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                                value={formData.employeeData.bloodGroup}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, bloodGroup: e.target.value }
                                                })}
                                            >
                                                <option value="">Select Blood Group</option>
                                                <option value="A+">A+</option>
                                                <option value="A-">A-</option>
                                                <option value="B+">B+</option>
                                                <option value="B-">B-</option>
                                                <option value="AB+">AB+</option>
                                                <option value="AB-">AB-</option>
                                                <option value="O+">O+</option>
                                                <option value="O-">O-</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="skillLevel">Skill Level</Label>
                                            <select
                                                id="skillLevel"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                                value={formData.employeeData.skillLevel}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, skillLevel: e.target.value }
                                                })}
                                            >
                                                <option value="beginner">Beginner</option>
                                                <option value="intermediate">Intermediate</option>
                                                <option value="advanced">Advanced</option>
                                                <option value="expert">Expert</option>
                                            </select>
                                        </div>
                                        <div>
                                            <Label htmlFor="employmentType">Employment Type</Label>
                                            <select
                                                id="employmentType"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                                value={formData.employeeData.employmentType}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, employmentType: e.target.value }
                                                })}
                                            >
                                                <option value="full_time">Full Time</option>
                                                <option value="part_time">Part Time</option>
                                                <option value="contract">Contract</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="empPanNumber">PAN Number</Label>
                                            <Input
                                                id="empPanNumber"
                                                value={formData.employeeData.panNumber}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, panNumber: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="aadharNumber">Aadhar Number</Label>
                                            <Input
                                                id="aadharNumber"
                                                value={formData.employeeData.aadharNumber}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, aadharNumber: e.target.value }
                                                })}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="bankName">Bank Name</Label>
                                            <Input
                                                id="bankName"
                                                value={formData.employeeData.bankName}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, bankName: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="bankBranch">Bank Branch</Label>
                                            <Input
                                                id="bankBranch"
                                                value={formData.employeeData.bankBranch}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, bankBranch: e.target.value }
                                                })}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="accountNumber">Account Number</Label>
                                            <Input
                                                id="accountNumber"
                                                value={formData.employeeData.accountNumber}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, accountNumber: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="ifscCode">IFSC Code</Label>
                                            <Input
                                                id="ifscCode"
                                                value={formData.employeeData.ifscCode}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, ifscCode: e.target.value }
                                                })}
                                                placeholder="e.g., SBIN0001234"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="paymentMode">Payment Mode</Label>
                                            <select
                                                id="paymentMode"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                                value={formData.employeeData.paymentMode}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, paymentMode: e.target.value }
                                                })}
                                            >
                                                <option value="neft">NEFT - National Electronic Funds Transfer</option>
                                                <option value="rtgs">RTGS - Real Time Gross Settlement</option>
                                                <option value="imps">IMPS - Immediate Payment Service</option>
                                                <option value="upi">UPI - Unified Payments Interface</option>
                                                <option value="cheque">Cheque</option>
                                                <option value="cash">Cash</option>
                                            </select>
                                        </div>
                                        <div></div>
                                    </div>

                                    {/* Address Section */}
                                    <div className="border-t pt-4 mt-4">
                                        <h4 className="font-semibold text-gray-900 mb-3">Address Information</h4>
                                        <div>
                                            <Label htmlFor="currentAddress">Current Address</Label>
                                            <textarea
                                                id="currentAddress"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                                rows="3"
                                                value={formData.employeeData.currentAddress}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, currentAddress: e.target.value }
                                                })}
                                            />
                                        </div>

                                        <div className="mt-3">
                                            <Label htmlFor="permanentAddress">Permanent Address</Label>
                                            <textarea
                                                id="permanentAddress"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                                                rows="3"
                                                value={formData.employeeData.permanentAddress}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    employeeData: { ...formData.employeeData, permanentAddress: e.target.value }
                                                })}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                                            <div>
                                                <Label htmlFor="city">City</Label>
                                                <Input
                                                    id="city"
                                                    value={formData.employeeData.city}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, city: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="state">State</Label>
                                                <Input
                                                    id="state"
                                                    value={formData.employeeData.state}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, state: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="pinCode">Pin Code</Label>
                                                <Input
                                                    id="pinCode"
                                                    value={formData.employeeData.pinCode}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, pinCode: e.target.value }
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Emergency Contact Section */}
                                    <div className="border-t pt-4 mt-4">
                                        <h4 className="font-semibold text-gray-900 mb-3">Emergency Contact Information</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="emergencyContactName">Contact Name</Label>
                                                <Input
                                                    id="emergencyContactName"
                                                    value={formData.employeeData.emergencyContactName}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, emergencyContactName: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="emergencyContactRelation">Relation</Label>
                                                <Input
                                                    id="emergencyContactRelation"
                                                    value={formData.employeeData.emergencyContactRelation}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, emergencyContactRelation: e.target.value }
                                                    })}
                                                    placeholder="e.g., Mother, Father, Spouse"
                                                />
                                            </div>
                                        </div>

                                        <div className="mt-3">
                                            <Label htmlFor="emergencyContactPhone">Contact Phone</Label>
                                            <Input
                                                id="emergencyContactPhone"
                                                type="tel"
                                                value={formData.employeeData.emergencyContactPhone}
                                                onChange={(e) => {
                                                    // Only allow digits
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, emergencyContactPhone: value }
                                                    });
                                                }}
                                                placeholder="10 digit number"
                                                maxLength={10}
                                            />
                                        </div>
                                    </div>

                                    {/* Photo Upload & Face Recognition Section */}
                                    <div className="border-t pt-4 mt-4">
                                        <h4 className="font-semibold text-gray-900 mb-3">Photo & Face Recognition</h4>

                                        {/* Photo Preview */}
                                        {photoPreview && (
                                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 relative">
                                                <button
                                                    type="button"
                                                    onClick={handleRemovePhoto}
                                                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition"
                                                    title="Remove photo"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                                <p className="text-sm font-medium text-gray-700 mb-2">Photo Preview:</p>
                                                <img
                                                    src={photoPreview}
                                                    alt="Preview"
                                                    className="h-32 w-32 object-cover rounded-lg border-2 border-blue-300"
                                                />
                                            </div>
                                        )}

                                        {/* Photo Upload */}
                                        <div>
                                            <Label htmlFor="photoUpload">Upload Photo</Label>
                                            <div className="flex items-center justify-center w-full">
                                                <label htmlFor="photoUpload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                        <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                                                        <p className="text-xs text-gray-500">PNG, JPG (Max 5MB)</p>
                                                    </div>
                                                    <input
                                                        id="photoUpload"
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={handlePhotoUpload}
                                                    />
                                                </label>
                                            </div>
                                        </div>

                                        {/* Face Recognition Toggle */}
                                        <div className={`mt-4 flex items-center justify-between p-3 rounded-lg border ${photoPreview ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-300'
                                            }`}>
                                            <div>
                                                <Label htmlFor="faceRecognitionEnabled" className="font-medium text-gray-900">Enable Face Recognition</Label>
                                                <p className="text-xs text-gray-600 mt-1">
                                                    {photoPreview
                                                        ? 'Enable facial recognition for this employee'
                                                        : 'Upload a photo first to enable face recognition'}
                                                </p>
                                            </div>
                                            <input
                                                id="faceRecognitionEnabled"
                                                type="checkbox"
                                                checked={formData.employeeData.faceRecognitionEnabled}
                                                onChange={(e) => {
                                                    if (!photoPreview && e.target.checked) {
                                                        toast({
                                                            title: 'Photo Required',
                                                            description: 'Please upload a photo before enabling face recognition',
                                                            variant: 'destructive'
                                                        });
                                                        return;
                                                    }
                                                    setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, faceRecognitionEnabled: e.target.checked }
                                                    });
                                                }}
                                                disabled={!photoPreview}
                                                className={`w-5 h-5 text-blue-600 rounded ${photoPreview ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                                    }`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate('/admin/users')}
                                    disabled={submitting}
                                    className="w-full sm:w-auto"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="mr-2 h-4 w-4" />
                                            Create User
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>

            {/* Image Crop Modal for Employee Photo */}
            {showCropModal && (
                <ImageCropModal
                    image={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onClose={handleCloseCropModal}
                />
            )}

            {/* Image Crop Modal for OEM Logo */}
            {showLogoCropModal && (
                <ImageCropModal
                    image={logoToCrop}
                    onCropComplete={handleLogoCropComplete}
                    onClose={handleCloseLogoCropModal}
                />
            )}
        </div>
    );
};

export default CreateUser;
